import type { IconName } from "@assetwatch/ui";
import camelCase from "lodash.camelcase";
import type { PageFlagKey } from "../components/LaunchDarkly/flags";

/**
 * What a page's `page.ts` exports — nothing about its data, and no name.
 *
 * Every page is one folder, `src/pages/<id>/`, and that folder is the page's
 * whole footprint: `page.ts` (this) is picked up by `registry.ts` for the
 * sidebar and landing page, and `route.tsx` by `vite.config.ts` for the
 * router. Nothing outside the folder lists it, so deleting the folder deletes
 * the page — and copying a folder (e.g. `example-page/`) makes a new one.
 *
 * The folder name is the page's id, and everything else is named from it: the
 * route (`/<id>`), the LaunchDarkly key (`mlFrontend-<id>`), the code flag
 * (`mlFrontend<Id>`) and the local preview list. It isn't repeated here, so a
 * copied folder never carries the old name.
 */
export type PageConfig = {
  /** Sidebar label and page header title. Defaults to the folder name, title-cased. */
  title?: string;
  /** One sentence, shown in the page header and on the landing page. */
  description: string;
  icon: IconName;
  /** Sidebar and landing-page position, lowest first. Ties sort by title. */
  order: number;
};

/** A page as the app sees it: its config plus the id from its folder name. */
export type PageDefinition = Required<PageConfig> & {
  /** The folder name, kebab-case, e.g. `hub-health`. */
  id: string;
};

/** `hub-health` → `Hub Health`. */
export function titleFromId(id: string): string {
  return id
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/** The route a page is served at. */
export function pagePath(page: PageDefinition): string {
  return `/${page.id}`;
}

/**
 * The page's flag as the React SDK serves it: the LaunchDarkly key
 * `mlFrontend-<id>` camel-cased by `lodash.camelcase`, the same function the
 * SDK uses, so the two can't disagree (`mp-3d-view` → `mlFrontendMp3DView`).
 */
export function pageFlagKey(page: PageDefinition): PageFlagKey {
  return camelCase(`mlFrontend-${page.id}`) as PageFlagKey;
}
